import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PlayIcon from "@heroicons/react/24/solid/PlayIcon";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { styled as styling } from "@mui/material/styles";
import Color from "../../../assets/colors";
import CurveArrow from "../../../assets/svg/curve-arrow.svg";

const desktopScrollMask =
  "linear-gradient(to right, #000 0%, #000 calc(100% - 6.5rem), rgba(0, 0, 0, 0.98) calc(100% - 5.85rem), rgba(0, 0, 0, 0.92) calc(100% - 5rem), rgba(0, 0, 0, 0.78) calc(100% - 3.8rem), rgba(0, 0, 0, 0.55) calc(100% - 2.4rem), rgba(0, 0, 0, 0.28) calc(100% - 1.2rem), rgba(0, 0, 0, 0) 100%)";

const mobileScrollMask =
  "linear-gradient(to right, #000 0%, #000 calc(100% - 3.5rem), rgba(0, 0, 0, 0.98) calc(100% - 3.05rem), rgba(0, 0, 0, 0.9) calc(100% - 2.55rem), rgba(0, 0, 0, 0.7) calc(100% - 1.75rem), rgba(0, 0, 0, 0.38) calc(100% - 0.9rem), rgba(0, 0, 0, 0) 100%)";

export const SkeletonContainer = styling("div")({
  width: "18.75rem",
  height: "13.5rem",
});

export const SocialMediaWrapper = styling("section")({
  position: "relative",
  width: "100%",
  padding: "2rem 0",

  "@media (max-width: 1280px)": {
    width: "100%",
    padding: "1.5rem 0",
  },
});

export const CardContainer = styling(Box, {
  shouldForwardProp: (prop) => prop !== "$isScrollable",
})((props) => ({
  display: "flex",
  position: "relative",
  gap: "1rem",
  width: "100%",
  boxSizing: "border-box",
  justifyContent: props.$isScrollable ? "flex-start" : "center",
  overflowX: props.$isScrollable ? "auto" : "visible",
  overflowY: "visible",
  padding: props.$isScrollable ? "0.25rem 0.25rem 0.5rem" : 0,
  scrollBehavior: "smooth",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorX: "contain",
  ...(props.$isScrollable && {
    WebkitMaskImage: desktopScrollMask,
    maskImage: desktopScrollMask,
    paddingBottom: "1rem",
  }),

  "@media (max-width:1280px)": {
    overflowX: "scroll",
    padding: props.$isScrollable ? "1.5rem 1.5rem 0.5rem" : "1.5rem 1rem 0",
    justifyContent: "flex-start",
  },

  "@media (max-width: 640px)": {
    padding: props.$isScrollable ? "1.5rem 1.5rem 0.5rem" : "1.5rem 1rem 0",

    ...(props.$isScrollable && {
      WebkitMaskImage: mobileScrollMask,
      maskImage: mobileScrollMask,
    }),
  },
}));

export const ArrowContainer = styling("div")((props) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",

  position: "absolute",
  top: props.$isScrollable ? "0.25rem" : "-25px",
  left: props.$isScrollable ? "-5rem" : 0,
  zIndex: 1,
  pointerEvents: "none",

  "@media (max-width: 1280px)": {
    alignItems: props.$more ? "center" : "flex-end",
    flexDirection: props.$isScrollable
      ? "column"
      : props.$more
      ? "row-reverse"
      : "column",
    top: props.$isScrollable ? "-14px" : props.$more ? "-10px" : "40%",
    left: props.$isScrollable ? "1rem" : props.$more ? "70%" : "10%",
  },

  "@media (max-width: 640px)": {
    left: props.$isScrollable ? "1rem" : props.$more ? "70%" : "10%",
    top: props.$isScrollable ? "-14px" : props.$more ? "-10px" : "40%",
  },
}));

export const CardStyled = styling("div", {
  shouldForwardProp: (prop) => prop !== "$isScrollable",
})((props) => ({
  position: "relative",
  borderRadius: "0.5rem",
  border: `2px solid ${Color.black}`,
  aspectRatio: "15.5 / 27.5",
  height: props.$isScrollable ? "auto" : "27.5rem",
  width: props.$isScrollable
    ? "clamp(13.75rem, calc((100% - 4rem) / 4.5), 15.5rem)"
    : "15.5rem",
  minWidth: props.$isScrollable
    ? "clamp(13.75rem, calc((100% - 4rem) / 4.5), 15.5rem)"
    : "15.5rem",
  flexShrink: 0,
  scrollSnapAlign: props.$isScrollable ? "start" : "none",

  transition: "box-shadow 0.1s ease-in-out, transform 0.1s ease-in-out",

  "@media (min-width: 1280px)": {
    "&:hover": {
      cursor: "pointer",
      border: `2px solid ${Color.black}`,
      boxShadow: "4px 4px 0px rgba(0, 0, 0, 1)",
      transform: "translate(-4px, -4px)",
    },
  },

  "@media (max-width: 1280px)": {
    width: props.$isScrollable
      ? "clamp(12rem, calc((100% - 3rem) / 3.5), 15.5rem)"
      : "15.5rem",
    minWidth: props.$isScrollable
      ? "clamp(12rem, calc((100% - 3rem) / 3.5), 15.5rem)"
      : "15.5rem",
  },

  "@media (max-width: 640px)": {
    aspectRatio: "7.5 / 13.5",
    height: props.$isScrollable ? "auto" : "13.5rem",
    width: props.$isScrollable
      ? "clamp(7.5rem, calc((100% - 2rem) / 2.25), 8.75rem)"
      : "7.5rem",
    minWidth: props.$isScrollable
      ? "clamp(7.5rem, calc((100% - 2rem) / 2.25), 8.75rem)"
      : "7.5rem",
  },
}));

export const DialogStyled = styling(Dialog)({});

export const PlayIconStyled = styling(PlayIcon)({
  color: "#fff",
  opacity: "0.8",
  height: "2.5rem",
  width: "2.5rem",
});

export const FontAwesomeIconStyled = styling(FontAwesomeIcon)({
  position: "absolute",
  top: "0.5rem",
  left: "0.5rem",
  color: "#fff",
  height: "1.25rem",
  width: "1.25rem",
});

export const ArrowIcon = styling(CurveArrow)((props) => ({
  "@media (max-width: 1280px)": {
    transform: props.$isScrollable
      ? "none"
      : props.$more
      ? "rotate(90deg)"
      : "rotate(-45deg)",
    margin: props.$isScrollable
      ? "0"
      : props.$more
      ? "0"
      : "-0.125rem -1rem 0 0",
  },
}));
